import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { CitationsPanel } from '../components/CitationBadge';
import type { Citation } from '../lib/types';
import { getUserToken } from '../lib/user-auth';

const STATUS_STYLE: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  awaiting_user_input: 'bg-amber-100 text-amber-900 border-amber-300',
  failed: 'bg-red-100 text-red-900 border-red-300',
  running: 'bg-blue-100 text-blue-900 border-blue-300',
};

export function RunDetailPage() {
  const { runId = '' } = useParams();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getRun>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getRun(runId)
      .then((r) => !cancelled && setData(r))
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [runId]);

  async function handlePrint() {
    const token = getUserToken();
    if (!token) return;
    const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
    const res = await fetch(`${base}/api/runs/${encodeURIComponent(runId)}/export.html`, {
      headers: { Authorization: `Bearer ${token.token}` },
    });
    const html = await res.text();
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  }

  if (loading) return <div className="text-taupe text-sm">Chargement…</div>;
  if (error) return <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>;
  if (!data) return <div>—</div>;

  const { run, citations } = data;
  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/runs" className="text-taupe text-sm hover:text-ink">← Retour à l'historique</Link>
        <button type="button" onClick={handlePrint} className="text-sm px-3 py-1.5 rounded border border-gold text-ink hover:bg-gold/10">
          📄 Exporter / Imprimer
        </button>
      </div>

      <header>
        <h2 className="font-serif text-3xl font-bold capitalize">{run.agent_name.replace(/_/g, ' ')}</h2>
        <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-taupe">
          <span className={`px-2 py-1 rounded border uppercase tracking-wider ${STATUS_STYLE[run.status] ?? 'bg-zinc-100 border-zinc-300'}`}>
            {run.status}
          </span>
          <span>Modèle <strong className="font-mono text-ink">{run.model_used}</strong></span>
          {run.latency_ms !== null && <span>Latence <strong className="font-mono text-ink">{run.latency_ms} ms</strong></span>}
          <span>Démarré <strong>{formatDate(run.started_at)}</strong></span>
          {run.completed_at && <span>Terminé <strong>{formatDate(run.completed_at)}</strong></span>}
          <span className="font-mono">ID {run.id}</span>
        </div>
      </header>

      {run.error_message && (
        <div className="card border border-red-300 bg-red-50">
          <div className="text-xs uppercase tracking-wider text-red-900 mb-2">Erreur</div>
          <pre className="text-sm whitespace-pre-wrap text-red-900">{run.error_message}</pre>
        </div>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Entrée (payload)</div>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-cream/40 border border-border rounded p-3 max-h-[500px] overflow-auto">
            {JSON.stringify(run.input_payload, null, 2)}
          </pre>
        </div>

        <div className="card">
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Sortie</div>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-cream/40 border border-border rounded p-3 max-h-[500px] overflow-auto">
            {run.output_payload ? JSON.stringify(run.output_payload, null, 2) : '(aucune sortie)'}
          </pre>
        </div>
      </section>

      <section className="card">
        <div className="text-xs uppercase tracking-wider text-taupe mb-3">
          Provenance · {citations.length} citation{citations.length > 1 ? 's' : ''}
        </div>
        <CitationsPanel citations={citations as Citation[]} />
      </section>

      <div className="text-center">
        <Link to={`/agents/${run.agent_name}`} className="text-sm text-gold underline">
          ↻ Re-jouer cet agent
        </Link>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return iso;
  }
}
